import Counter from './Counter'
import { Trans, useTranslation } from 'react-i18next'
import debounce from 'lodash.debounce'
import { SelectedPassengers } from '../../Hooks'
import { useCallback, useEffect, useState } from 'react'
import OverlayFooter from '../Shared/OverlayFooter'
import { Toggle } from '../Shared/Toggle'
import Markdown from '../Shared/Markdown'
import { isPetsOnlyAllowedInVehicle } from '../../Utils/passengers'
import { useRoutes } from '../../Hooks/routes/useRoutes'
import { useVehicles } from '../../Hooks/vehicles/useVehicles'
import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { ActiveSelectField, useActiveSelectField } from '../../context/ActiveSelectFieldProvider'

const Passengers: React.FC = () => {
  const { t } = useTranslation()
  const { selectedRoute } = useRoutes()
  const { petsAllowed, maxNumberOfPassengers, vehicleApiData, selectedPassengers: initialPassengers } = useVehicles()
  const { dispatch } = useTravelSearchContext()
  const { closeIfActive } = useActiveSelectField()

  const [passengers, setPassengers] = useState<SelectedPassengers>({
    adults: initialPassengers?.adults ?? 1,
    children: initialPassengers?.children ?? 0,
    pets: initialPassengers?.pets ?? 0,
    childrenAges: initialPassengers?.childrenAges ?? [],
  })

  const maxAllowedPets = 5

  const limit = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

  const debounceQuantityChange = useCallback(
    debounce((adults: number, children: number, pets: number, childrenAges: number[]) => {
      dispatch({
        type: 'SET_PASSENGERS',
        passengers: { adults, children, pets, childrenAges },
      })
    }, 400),
    []
  )

  useEffect(() => {
    if (passengers.pets > vehicleApiData?.maxNumberOfPets) {
      const adjustedPets = vehicleApiData.maxNumberOfPets
      setPassengers((prev) => ({ ...prev, pets: adjustedPets }))
      debounceQuantityChange(passengers.adults, passengers.children, adjustedPets, passengers.childrenAges)
    }
  }, [vehicleApiData?.maxNumberOfPets])

  useEffect(() => {
    if (!petsAllowed && passengers.pets > 0) {
      setPassengers((prev) => ({ ...prev, pets: 0 }))
      debounceQuantityChange(passengers.adults, passengers.children, 0, passengers.childrenAges)
    }
  }, [petsAllowed])

  useEffect(() => {
    const total = passengers.adults + passengers.children
    if (maxNumberOfPassengers && total > maxNumberOfPassengers) {
      const adjustedChildren = Math.max(0, maxNumberOfPassengers - passengers.adults)
      const adjustedAges = passengers.childrenAges.slice(0, adjustedChildren)
      setPassengers({
        ...passengers,
        adults: maxNumberOfPassengers - adjustedChildren,
        children: adjustedChildren,
        childrenAges: adjustedAges,
      })
    }
  }, [maxNumberOfPassengers])

  const handleChange = (field: keyof SelectedPassengers, value: number) => {
    let newPassengers = { ...passengers, [field]: value }
    if (field === 'children') {
      newPassengers.childrenAges =
        value > passengers.childrenAges.length
          ? [...passengers.childrenAges, ...Array(value - passengers.childrenAges.length).fill(8)]
          : passengers.childrenAges.slice(0, value)
    }
    setPassengers(newPassengers)
    debounceQuantityChange(newPassengers.adults, newPassengers.children, newPassengers.pets, newPassengers.childrenAges)
  }

  const handleAgeChange = (ages: number[]) => {
    setPassengers((prev) => ({ ...prev, childrenAges: ages }))
    debounceQuantityChange(passengers.adults, passengers.children, passengers.pets, ages)
  }

  const getPassengerErrorMessage = (max: number, route: string): string => {
    const key = `PASSENGERS-ERRORMESSAGE-${route}`
    const msg = t(key)
    return msg !== key ? msg : t('PASSENGERS-ERRORMESSAGE', { n1: max })
  }

  return (
    <div className="ts-min-w-[260px] ts-text-dfds-text-dark-primary md:ts-min-w-[512px]">
      <ul className="ts-m-0 ts-p-dfds-s ts-border-0 ts-border-solid ts-list-none">
        <li data-e2e="li-adults" className="ts-list-none ts-p-0 ts-m-0 ts-flex ts-h-[50px] ts-flex-row ts-items-center">
          <Counter
            e2eAttribute="adults"
            title={t('ADULTS')}
            description="+16"
            value={passengers.adults}
            downDisabled={passengers.adults === 1}
            upDisabled={passengers.adults + passengers.children === maxNumberOfPassengers}
            onValueChange={(val) => handleChange('adults', limit(Number(val), 1, maxNumberOfPassengers - passengers.children))}
          />
        </li>
        <div className="ts-mb-dfds-s ts-border-b ts-border-0 ts-border-solid ts-border-dfds-divider-dark ts-my-dfds-s"></div>
        <li data-e2e="li-children" className="ts-p-0 ts-m-0 ts-list-none ts-flex ts-h-[50px] ts-flex-row ts-items-center">
          <Counter
            e2eAttribute="children"
            title={t('CHILDREN')}
            description="0-15"
            value={passengers.children}
            downDisabled={passengers.children === 0}
            upDisabled={passengers.adults + passengers.children === maxNumberOfPassengers}
            onValueChange={(val) => handleChange('children', limit(Number(val), 0, maxNumberOfPassengers - passengers.adults))}
          />
        </li>
        {passengers.childrenAges.map((age, i) => (
          <li data-e2e="li-child-age" key={i} className="ts-p-0 ts-m-0 ts-list-none ts-flex ts-h-[50px] ts-flex-row ts-items-center">
            <Counter
              e2eAttribute={`child-age-${i + 1}`}
              title={t('CHILD-AGE', { n1: i + 1 })}
              value={age}
              downDisabled={age === 0}
              upDisabled={age === 15}
              onValueChange={(val) => {
                const updatedAges = [...passengers.childrenAges]
                updatedAges[i] = limit(Number(val), 0, 15)
                handleAgeChange(updatedAges)
              }}
            />
          </li>
        ))}
        {selectedRoute && passengers.adults + passengers.children === maxNumberOfPassengers && (
          <li data-e2e="pax-error-message" className="ts-p-0 ts-m-0 ts-list-none ts-mt-dfds-s">
            <label className="ts-phone-number ts-mb-dfds-s ts-leading-relaxed ts-text-dfds-status-alert">
              <Markdown input={getPassengerErrorMessage(maxNumberOfPassengers, selectedRoute.routeCode)} />
            </label>
          </li>
        )}
      </ul>
      <div className="ts-border-b ts-border-solid ts-border-0 ts-border-dfds-divider-dark"></div>
      <div className="ts-p-dfds-s ts-flex ts-flex-row ts-items-center ts-gap-dfds-s">
        <div className="ts-flex ts-flex-col">
          <label className="ts-font-medium ts-text-cvi-brand-brand900">{t('PETS-TOGGLE-TITLE')}</label>
          {petsAllowed ? (
            <div className="ts-font-normal ts-text-sm ts-mt-dfds-xs ts-text-cvi-grey-grey800">
              <Trans i18nKey={'MAX-COUNT-PETS'} count={maxAllowedPets}>
                {{ maxAllowedPets }}
              </Trans>
              . {selectedRoute && isPetsOnlyAllowedInVehicle(selectedRoute.routeCode) ? t('PETS-ONLY-ALLOWED-IN-VEHICLE') + '. ' : ''}
              {t('FEES-APPLY')}
            </div>
          ) : (
            <div data-e2e="pets-error-message" className="ts-font-normal ts-text-sm ts-mt-dfds-xs ts-text-dfds-status-alert">
              {t('PETS-ERRORMESSAGE')}
            </div>
          )}
        </div>
        <div className="ts-ml-auto">
          <Toggle
            name="pets"
            disabled={!petsAllowed}
            defaultChecked={passengers.pets > 0}
            onCheckedChange={(checked) => handleChange('pets', checked ? 5 : 0)}
          />
        </div>
      </div>
      <div className="ts-border-b ts-border-0 ts-border-solid ts-border-dfds-divider-dark lg:ts-hidden"></div>
      <OverlayFooter onClick={() => closeIfActive(ActiveSelectField.PASSENGER)} />
    </div>
  )
}

export default Passengers
